import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const themes = {
  light: {
    name: 'light',
    colors: {
      primary: '#8B2635', primaryDark: '#6B1D29', secondary: '#2C5530', secondaryLight: '#3D7A44',
      accent: '#D4A84B', dark: '#1A1A1A', light: '#FDF8F3', cream: '#FAF6F0', white: '#FFFFFF',
      gray: '#666666', lightGray: '#E8E4DF', background: '#FFFFFF', surface: '#FDF8F3',
      text: '#1A1A1A', textSecondary: '#666666', border: '#E8E4DF', shadow: 'rgba(0, 0, 0, 0.1)'
    }
  },
  dark: {
    name: 'dark',
    colors: {
      primary: '#C44D5D', primaryDark: '#A83D4B', secondary: '#4CAF50', secondaryLight: '#66BB6A',
      accent: '#FFD54F', dark: '#FFFFFF', light: '#1E1E1E', cream: '#2D2D2D', white: '#121212',
      gray: '#B0B0B0', lightGray: '#3D3D3D', background: '#121212', surface: '#1E1E1E',
      text: '#FFFFFF', textSecondary: '#B0B0B0', border: '#3D3D3D', shadow: 'rgba(0, 0, 0, 0.5)'
    }
  },
  mint: {
    name: 'mint',
    colors: {
      primary: '#1A936F', primaryDark: '#147356', secondary: '#88D498', secondaryLight: '#A8E6B5',
      accent: '#F6AE2D', dark: '#1A1A1A', light: '#F0FFF4', cream: '#E8F5E9', white: '#FFFFFF',
      gray: '#666666', lightGray: '#C8E6C9', background: '#FFFFFF', surface: '#F0FFF4',
      text: '#1A1A1A', textSecondary: '#666666', border: '#C8E6C9', shadow: 'rgba(0, 0, 0, 0.1)'
    }
  },
  ocean: {
    name: 'ocean',
    colors: {
      primary: '#1565C0', primaryDark: '#0D47A1', secondary: '#00ACC1', secondaryLight: '#26C6DA',
      accent: '#FFB74D', dark: '#1A1A1A', light: '#E3F2FD', cream: '#BBDEFB', white: '#FFFFFF',
      gray: '#666666', lightGray: '#90CAF9', background: '#FFFFFF', surface: '#E3F2FD',
      text: '#1A1A1A', textSecondary: '#666666', border: '#90CAF9', shadow: 'rgba(0, 0, 0, 0.1)'
    }
  }
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('p3dental-theme');
      return saved && themes[saved] ? saved : 'light';
    }
    return 'light';
  });

  useEffect(() => {
    localStorage.setItem('p3dental-theme', theme);
    const root = document.documentElement;
    const themeColors = themes[theme].colors;
    Object.entries(themeColors).forEach(([key, value]) => {
      const cssVarName = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVarName, value);
    });
    document.body.style.backgroundColor = themeColors.background;
    document.body.style.color = themeColors.text;
  }, [theme]);

  const setSpecificTheme = (themeName) => {
    if (themes[themeName]) setTheme(themeName);
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, themeConfig: themes[theme], setTheme: setSpecificTheme,
      availableThemes: Object.keys(themes)
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within a ThemeProvider');
  return context;
}

export default ThemeContext;
